/**
 * Outstanding, as cards.
 *
 * The desktop table hides five ageing columns on a narrow screen, which is
 * degradation rather than design. On a phone the owner wants one thing per
 * party -- how much, and how bad -- so each party becomes a card with a
 * proportional ageing bar instead of five numbers they cannot read.
 */
import { useState } from 'react'
import {
  Box, Chip, Collapse, Paper, Stack, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { BUCKETS, type Bucket, type PartyOutstanding } from '../api/types'
import { c, fmtMoney } from '../theme'

const TONE: Record<Bucket, string> = {
  current: c.chart2, d1_30: c.chart1, d31_60: c.warning,
  d61_90: c.dangerInk, d90_plus: c.dangerInk,
}

export default function OutstandingCards(
  { parties, onVoucher }: {
    parties: PartyOutstanding[]
    onVoucher?: (id: number) => void
  },
) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<string | null>(null)

  return (
    <Stack spacing={1} sx={{ p: 1.5 }}>
      {parties.map((p) => {
        const total = Number(p.total) || 1
        const expanded = open === p.ledger_code
        return (
          <Paper key={p.ledger_code} variant="outlined"
            onClick={() => setOpen(expanded ? null : p.ledger_code)}
            sx={{ p: 1.5, cursor: 'pointer' }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap>
                  {p.ledger_name}
                </Typography>
                <span className="num" style={{ fontSize: 11, color: c.inkMuted }}>
                  {p.ledger_code}
                </span>
              </Box>
              <Box sx={{ textAlign: 'end' }}>
                <span className="num" style={{ fontSize: 17, fontWeight: 600 }}>
                  {fmtMoney(p.total)}
                </span>
                {Number(p.overdue) > 0 && (
                  <Typography sx={{ fontSize: 10.5, color: c.dangerInk }}>
                    {fmtMoney(p.overdue)} {t('outstanding.overdueWord')}
                  </Typography>
                )}
              </Box>
            </Stack>

            {/* Proportional ageing bar: the shape of the debt at a glance,
                without asking anyone to read five columns on a phone. */}
            <Stack direction="row" sx={{ mt: 1.25, height: 5, borderRadius: 3,
              overflow: 'hidden', bgcolor: c.divider }}>
              {BUCKETS.map((b) => {
                const v = Number(p.buckets[b])
                if (v <= 0) return null
                return <Box key={b} sx={{ width: `${(v / total) * 100}%`,
                  bgcolor: TONE[b] }} />
              })}
            </Stack>

            <Collapse in={expanded}>
              <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                {p.bills.map((b) => (
                  <Stack key={b.ref_name} direction="row" spacing={1}
                    alignItems="center"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (b.voucher_id) onVoucher?.(b.voucher_id)
                    }}
                    sx={{ py: 0.5, borderTop: `1px solid ${c.divider}` }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5 }} noWrap>
                        {b.ref_name}</Typography>
                      <Typography sx={{ fontSize: 10.5,
                        color: b.days_overdue ? c.dangerInk : c.inkMuted }}>
                        {b.days_overdue
                          ? t('bills.daysOverdue', { n: b.days_overdue })
                          : `${t('bills.due')} ${b.due_date ?? b.bill_date}`}
                      </Typography>
                    </Box>
                    <span className="num" style={{ fontSize: 13 }}>
                      {fmtMoney(b.outstanding)}</span>
                  </Stack>
                ))}
                {Number(p.on_account) < 0 && (
                  <Chip size="small" label={t('outstanding.advance')}
                    sx={{ alignSelf: 'flex-start', height: 19, fontSize: 10,
                      bgcolor: c.primarySoft, color: c.primaryInk }} />
                )}
              </Stack>
            </Collapse>
          </Paper>
        )
      })}
    </Stack>
  )
}
